import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import { getFileUrl } from '../../utils/fileUtils';
import AdminNavbar from '../../components/admin/AdminNavbar';
import HtmlEditor from '../../components/admin/HtmlEditor';
import './AdminCommon.css';

function claveToSlug(clave) {
  return (clave || '').replace(/_/g, '-');
}

const AdminSeccionesDirectorio = () => {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(null);
  const [titulo, setTitulo] = useState('');
  const [texto, setTexto] = useState('');
  const [telefono, setTelefono] = useState('');
  const [direccion, setDireccion] = useState('');
  const [urlExterna, setUrlExterna] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [unaSolaFoto, setUnaSolaFoto] = useState(true);
  const [imagenFile, setImagenFile] = useState(null);
  const [nuevaImagenFile, setNuevaImagenFile] = useState(null);

  const { data: secciones = {}, isLoading } = useQuery({
    queryKey: ['secciones-directorio-admin'],
    queryFn: async () => {
      const res = await api.get('/secciones-directorio/admin/all');
      return res.data;
    }
  });

  const updateMutation = useMutation({
    mutationFn: async ({ clave, titulo, texto, telefono, direccion, url_externa, video_url, una_sola_foto, imagen }) => {
      const formData = new FormData();
      if (titulo !== undefined) formData.append('titulo', titulo);
      if (texto !== undefined) formData.append('texto', texto);
      if (telefono !== undefined) formData.append('telefono', telefono);
      if (direccion !== undefined) formData.append('direccion', direccion);
      if (url_externa !== undefined) formData.append('url_externa', url_externa);
      if (video_url !== undefined) formData.append('video_url', video_url);
      formData.append('una_sola_foto', una_sola_foto ? '1' : '0');
      if (imagen) formData.append('imagen', imagen);
      const res = await api.put(`/secciones-directorio/${clave}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['secciones-directorio-admin']);
      setEditing(null);
      setImagenFile(null);
      alert('Sección actualizada correctamente.');
    },
    onError: (e) => alert('Error: ' + (e.response?.data?.error || e.message))
  });

  const addImageMutation = useMutation({
    mutationFn: async ({ clave, imagen }) => {
      const formData = new FormData();
      formData.append('imagen', imagen);
      await api.post(`/secciones-directorio/${clave}/imagenes`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['secciones-directorio-admin']);
      setNuevaImagenFile(null);
      alert('Imagen añadida.');
    },
    onError: (e) => alert('Error: ' + (e.response?.data?.error || e.message))
  });

  const deleteImageMutation = useMutation({
    mutationFn: async ({ clave, imagenId }) => {
      await api.delete(`/secciones-directorio/${clave}/imagenes/${imagenId}`);
    },
    onSuccess: () => queryClient.invalidateQueries(['secciones-directorio-admin']),
    onError: (e) => alert('Error: ' + (e.response?.data?.error || e.message))
  });

  const openEdit = (clave) => {
    const s = secciones[clave];
    setEditing(clave);
    setTitulo(s?.titulo || '');
    setTexto(s?.texto || '');
    setTelefono(s?.telefono || '');
    setDireccion(s?.direccion || '');
    setUrlExterna(s?.url_externa || '');
    setVideoUrl(s?.video_url || '');
    setUnaSolaFoto(s?.una_sola_foto === 1);
    setImagenFile(null);
    setNuevaImagenFile(null);
  };

  const handleSave = (clave) => {
    updateMutation.mutate({
      clave,
      titulo,
      texto,
      telefono,
      direccion,
      url_externa: urlExterna.trim() || '',
      video_url: videoUrl.trim() || '',
      una_sola_foto: unaSolaFoto,
      imagen: imagenFile || undefined
    });
  };

  const handleAddImage = (clave) => {
    if (!nuevaImagenFile) { alert('Seleccione una imagen.'); return; }
    addImageMutation.mutate({ clave, imagen: nuevaImagenFile });
  };

  const clavesOrdenadas = Object.keys(secciones).sort(
    (a, b) => (secciones[a]?.orden ?? 0) - (secciones[b]?.orden ?? 0)
  );

  const porGrupo = {};
  clavesOrdenadas.forEach(clave => {
    const g = secciones[clave]?.grupo || 'Otros';
    if (!porGrupo[g]) porGrupo[g] = [];
    porGrupo[g].push(clave);
  });
  const gruposOrdenados = [...new Set(['Curia diocesana', 'Medios de comunicación', 'Soluciones de negocio', 'Otros servicios', 'Otros'].filter(g => porGrupo[g]?.length))];
  Object.keys(porGrupo).filter(g => !gruposOrdenados.includes(g)).forEach(g => gruposOrdenados.push(g));

  if (isLoading) return <div className="admin-common"><AdminNavbar /><div className="container">Cargando...</div></div>;

  if (clavesOrdenadas.length === 0) {
    return (
      <div className="admin-common">
        <AdminNavbar />
        <div className="container">
          <div className="admin-header">
            <Link to="/admin" className="btn btn-secondary">← Panel</Link>
            <h1>Secciones Directorio</h1>
            <div className="admin-empty-directorio">
              <p><strong>No hay secciones cargadas.</strong> Ejecute el script SQL para crear y poblar la tabla:</p>
              <pre><code>mysql -u USUARIO -p NOMBRE_BD &lt; database/init_secciones_directorio.sql</code></pre>
              <p className="admin-empty-hint">Reemplace USUARIO y NOMBRE_BD por sus datos de conexión. El script crea la tabla y agrega Curia, Medios y Soluciones de negocio.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-common">
      <AdminNavbar />
      <div className="container">
        <div className="admin-header">
          <Link to="/admin" className="btn btn-secondary">← Panel</Link>
          <h1>Secciones Directorio</h1>
          <p>Editar título, descripción (HTML), teléfono, dirección, imagen y enlace externo de cada ítem del menú Directorio. Clero mantiene /clero.</p>
        </div>

        <div className="secciones-pastoral-admin">
          {gruposOrdenados.map(grupo => (
            <div key={grupo} className="seccion-pastoral-grupo">
              <h3 className="seccion-pastoral-grupo-title">{grupo}</h3>
              <div className="seccion-pastoral-grupo-items">
                {porGrupo[grupo]?.map(clave => {
                  const s = secciones[clave];
                  const slug = claveToSlug(clave);
                  const esClero = clave === 'clero_diocesano';
                  return (
                    <div key={clave} className="seccion-pastoral-card">
                      <h2>{s?.titulo || clave}</h2>
                      <p className="seccion-pastoral-slug">
                        {esClero ? '/clero (directo)' : `/directorio/${slug}`}
                      </p>
                      {editing === clave ? (
                        <div className="seccion-edit-form">
                          <label>Título</label>
                          <input type="text" value={titulo} onChange={e => setTitulo(e.target.value)} />

                          <HtmlEditor label="Texto / Descripción (HTML)" value={texto} onChange={setTexto} rows={6} placeholder="Descripción del servicio..." />

                          <label>URL externa (TV, radios: abre en nueva ventana)</label>
                          <input type="text" value={urlExterna} onChange={e => setUrlExterna(e.target.value)} placeholder="/tv-ipiales-en-vivo o https://..." />

                          {!esClero && <label>URL de video de YouTube</label>}
                          {!esClero && <input type="url" value={videoUrl} onChange={e => setVideoUrl(e.target.value)} placeholder="https://www.youtube.com/watch?v=..." />}

                          <label>Teléfono</label>
                          <input type="text" value={telefono} onChange={e => setTelefono(e.target.value)} placeholder="Ej: +57 312 123 4567" />

                          <label>Dirección</label>
                          <input type="text" value={direccion} onChange={e => setDireccion(e.target.value)} placeholder="Dirección de contacto" />

                          {!esClero && (
                            <label>
                              <input type="checkbox" checked={unaSolaFoto} onChange={e => setUnaSolaFoto(e.target.checked)} />
                              Una sola foto. Si no, galería (varias imágenes como slider).
                            </label>
                          )}

                          {esClero ? (
                            <>
                              <label>Imagen</label>
                              <input type="file" accept="image/*" onChange={e => setImagenFile(e.target.files?.[0] || null)} />
                            </>
                          ) : unaSolaFoto ? (
                            <>
                              <label>Imagen (reemplaza la actual)</label>
                              <input type="file" accept="image/*" onChange={e => setImagenFile(e.target.files?.[0] || null)} />
                            </>
                          ) : (
                            <>
                              <label>Imágenes actuales</label>
                              <div className="casa-imagenes-list">
                                {(s?.imagenes || []).filter(img => img.id !== 0).map((img) => (
                                  <div key={img.id} className="casa-imagen-item">
                                    <img src={getFileUrl(img.imagen_url)} alt="" />
                                    <button type="button" className="btn btn-danger btn-sm" onClick={() => deleteImageMutation.mutate({ clave, imagenId: img.id })} disabled={deleteImageMutation.isLoading}>Eliminar</button>
                                  </div>
                                ))}
                              </div>
                              <label>Añadir otra imagen</label>
                              <div className="casa-add-imagen">
                                <input type="file" accept="image/*" onChange={e => setNuevaImagenFile(e.target.files?.[0] || null)} />
                                <button type="button" className="btn btn-primary" onClick={() => handleAddImage(clave)} disabled={!nuevaImagenFile || addImageMutation.isLoading}>Subir imagen</button>
                              </div>
                            </>
                          )}

                          <div className="form-actions">
                            <button type="button" className="btn btn-primary" onClick={() => handleSave(clave)} disabled={updateMutation.isLoading}>Guardar</button>
                            <button type="button" className="btn btn-secondary" onClick={() => setEditing(null)}>Cancelar</button>
                          </div>
                        </div>
                      ) : (
                        <>
                          {(s?.imagenes?.length > 0 || s?.imagen_url) && (
                            <div className="seccion-preview-img">
                              {s.imagenes?.length > 0 ? s.imagenes.slice(0, 3).map(img => (
                                <img key={img.id || img.imagen_url} src={getFileUrl(img.imagen_url)} alt="" />
                              )) : <img src={getFileUrl(s.imagen_url)} alt={s.titulo} />}
                              {s.imagenes?.length > 3 && <span>+{s.imagenes.length - 3} más</span>}
                            </div>
                          )}
                          {s?.url_externa && <p className="seccion-url-externa"><strong>Enlace:</strong> {s.url_externa}</p>}
                          {(s?.telefono || s?.direccion || s?.texto) && (
                            <div className="seccion-preview-meta">
                              {s.telefono && <p><strong>Teléfono:</strong> {s.telefono}</p>}
                              {s.direccion && <p><strong>Dirección:</strong> {s.direccion}</p>}
                              {s.texto && <p className="texto-corto">{(s.texto || '').replace(/<[^>]+>/g, ' ').trim().substring(0, 120)}...</p>}
                            </div>
                          )}
                          <p className="seccion-pastoral-tipo">{s?.una_sola_foto === 1 ? 'Una sola foto' : 'Galería'}</p>
                          <div className="seccion-pastoral-actions">
                            <button type="button" className="btn" onClick={() => openEdit(clave)}>Editar</button>
                            {!esClero && (
                              <Link to={`/directorio/${slug}`} className="btn" target="_blank" rel="noopener noreferrer">Ver página</Link>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminSeccionesDirectorio;
